import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, LoginCredentials, AuthContextType } from '../types/auth';
import { authService } from '../services/authService';
import { tokenStorage, userStorage, clearAuthStorage } from '../utils/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresSetup, setRequiresSetup] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // First check if setup is required
        console.log('Checking setup status...');
        const setupStatus = await authService.getSetupStatus();
        console.log('Setup status response:', setupStatus);
        setRequiresSetup(setupStatus.requiresSetup);
        setSetupLoading(false);

        // If setup is required, don't try to authenticate
        if (setupStatus.requiresSetup) {
          console.log('Setup required, skipping authentication');
          setIsLoading(false);
          return;
        }

        const storedToken = tokenStorage.get();
        const storedUser = userStorage.get();

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(storedUser);
          
          // Verify token is still valid by fetching profile
          try {
            const profile = await authService.getProfile();
            setUser(profile);
            userStorage.set(profile);
          } catch (error) {
            // Token is invalid, clear storage
            clearAuthStorage();
            setToken(null);
            setUser(null);
          }
        }
      } catch (error) {
        // If we can't check setup status, assume setup is required
        console.error('Failed to check setup status:', error);
        console.log('Assuming setup is required due to error');
        setRequiresSetup(true);
        setSetupLoading(false);
      }
      
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // NOTE: do NOT toggle the global `isLoading` here. SetupGuard (which wraps
  // every route, including /login) replaces the whole tree with a full-screen
  // spinner while `isLoading` is true — toggling it during submit unmounts the
  // LoginForm mid-flight, wiping its state, so failures looked like a silent
  // page refresh instead of showing an error. The form tracks its own
  // submitting state; `isLoading` is reserved for the initial auth bootstrap.
  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      const response = await authService.login(credentials);

      setToken(response.token);
      setUser(response.user);

      tokenStorage.set(response.token);
      userStorage.set(response.user);
    } catch (error) {
      // Don't redirect on login errors - let the component handle the error display
      console.error('Login failed in AuthContext:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Call backend logout to log the action
      await authService.logout();
    } catch (error) {
      // Don't fail logout if backend call fails
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local state and storage
      setToken(null);
      setUser(null);
      clearAuthStorage();
    }
  };

  const refreshSetupStatus = async (): Promise<void> => {
    try {
      const setupStatus = await authService.getSetupStatus();
      setRequiresSetup(setupStatus.requiresSetup);
    } catch (error) {
      console.error('Failed to refresh setup status:', error);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user && !!token,
    requiresSetup,
    setupLoading,
    refreshSetupStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};